import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TextField } from '@mui/material'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import TableShared from '../../components/TableShared'
import Loading from '../../components/Loading'
import quoteStatusAPI from '../../services/api/Quotes/quoteStatusAPI'
import { handleAxiosError, isValid, canViewPrice, formatCurrency, formatQuantity } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'
import { useAuth } from '../../hooks/auth'

const ShowQuote = () => {

    const { user } = useAuth({ middleware: 'guest' })
    const { t } = useTranslation()
    const { config, features, currencySymbol, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)
    const [quoteNumber, setQuoteNumber] = useState('')
    const [quoteItems, setQuoteItems] = useState([])
    const [quoteStatuses, setQuoteStatuses] = useState([])

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [shipToLocations, setShipToLocations] = useState([])
    const [shipToAddressId, setShipToAddressId] = useState('')
    const [address, setAddress] = useState('')
    const [zip, setZip] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [salespersonId, setSalespersonId] = useState('')
    const [salespersonName, setSalespersonName] = useState('')
    const [quoteDate, setQuoteDate] = useState(dayjs())
    const [validUntil, setValidUntil] = useState(dayjs())
    const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(dayjs())
    const [quoteStatus, setQuoteStatus] = useState('draft')
    const [customerReference, setCustomerReference] = useState('')
    const [comments, setComments] = useState('')

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/quotes')
    }

    /* KEY EVENT */
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        getQuote()
    }, [params.id])

    /* real-time: refresh if another user updates this quote, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-quote-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-quote-deleted`)

        channelUpdated.bind(`${clientId}-quote-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getQuote()
            }
        })

        channelDeleted.bind(`${clientId}-quote-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-quote-updated`)
            pusher.unsubscribe(`${clientId}-quote-deleted`)
        })
    }, [])

    useEffect(() => {
        getQuoteStatuses()
    }, [])

    useEffect(() => {
        if (isValid(customerId)) {
            getShipToLocations(customerId)
        }
    }, [customerId])

    useEffect(() => {
        if (shipToLocations.length > 0 && shipToAddressId) {
            const shipToLocation = shipToLocations.find(target => target.id === shipToAddressId)
            if (shipToLocation) {
                setAddress(shipToLocation.address)
                setZip(shipToLocation.zip)
                setCity(shipToLocation.city)
                setState(shipToLocation.state)
            }
        }
    }, [shipToLocations, shipToAddressId])

    /* requests */

    const getQuote = async () => {
        setIsLoading(true)
        await axios.get(`/api/quotes/${params.id}`, config)
            .then(res => {
                const quote = res.data.data

                setQuoteNumber(quote?.quote_number ?? `Q-${quote?.id ?? params.id}`)
                setCustomerId(quote?.customer_id ?? '')
                setCustomerName(quote?.customer_name ?? '')
                setShipToAddressId(quote?.ship_address_id ?? '')
                setSalespersonId(quote?.salesperson_id ?? '')
                setSalespersonName(quote?.salesperson_name ?? '')
                setQuoteDate(quote?.quote_date ? dayjs(quote.quote_date) : dayjs())
                setValidUntil(quote?.valid_until ? dayjs(quote.valid_until) : dayjs())
                setRequestedDeliveryDate(quote?.requested_delivery_date ? dayjs(quote.requested_delivery_date) : dayjs())
                setQuoteStatus(quote?.quote_status ?? 'draft')
                setCustomerReference(quote?.customer_reference ?? '')
                setComments(quote?.comments ?? '')
                setQuoteItems(quote?.quote_items ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getQuoteStatuses = async () => {
        await quoteStatusAPI.getQuoteStatuses(config)
            .then((data) => {
                setQuoteStatuses(data)
            })
    }

    const getShipToLocations = async (id) => {
        await axios
            .get(`/api/ship-addresses?customer_id=${id}`, config)
            .then(res => {
                setShipToLocations(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getColumns = () => {
        return [
            {
                field: 'item_no',
                headerName: t('item__no'),
                flex: 0.8,
                minWidth: 90
            },
            {
                field: 'description',
                headerName: t('description'),
                flex: 1.5,
                minWidth: 140
            },
            {
                field: 'unit_of_measure_code',
                headerName: t('uom'),
                flex: 0.7,
                minWidth: 80,
                align: 'center',
                headerAlign: 'center'
            },
            {
                field: 'quantity',
                headerName: t('quantity'),
                flex: 1,
                minWidth: 110,
                align: 'center',
                headerAlign: 'center',
                valueFormatter: ({ value }) => isValid(value) ? formatQuantity(value) : ''
            },
            {
                field: 'source_vendor_name',
                headerName: t('vendor'),
                flex: 1.2,
                minWidth: 120
            },
            ...(canViewPrice(user, features) ? [
                {
                    field: 'source_cost',
                    headerName: t('source_cost'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'freight_cost',
                    headerName: t('freight_cost'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'fuel_surcharge',
                    headerName: t('fuel_surcharge'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'other_costs',
                    headerName: t('other_costs'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'calculated_cost',
                    headerName: t('calculated_cost'),
                    flex: 1,
                    minWidth: 120,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'unit_price',
                    headerName: t('unit_price'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'total_price',
                    headerName: t('total_price'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'margin_dollar',
                    headerName: t('margin_dollar'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'margin_percent',
                    headerName: t('margin_percent'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${formatCurrency(value)}%` : t('coming_soon')
                }
            ] : [])
        ]
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('view_quote')} - {quoteNumber}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div className='p-5 pb-12 flex justify-between gap-3 flex-col lg:flex-row'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full lg:w-2/5 h-fit edit-view'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        {/* Quote No. */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_no')}</p>
                            <div className='w-full'>
                                <TextField sx={TEXTFIELD_STYLE} value={quoteNumber} disabled />
                            </div>
                        </div>

                        {/* Customer */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('customer')}</p>
                            <div className='w-full'>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    disableClearable
                                    options={[]}
                                    value={{ label: customerName, id: null }}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>
                        </div>

                        {/* Ship to location */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('ship_to_location')}</p>
                            <div className='w-full'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select disabled value={shipToAddressId} sx={TEXTFIELD_STYLE}>
                                        {shipToLocations?.map((item, index) => <MenuItem key={index} value={item.id}>{item.code} - {item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        {shipToAddressId ? (
                            <div className='w-full pb-4'>
                                <TextField
                                    disabled
                                    multiline
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    value={`${address}\n${zip} ${city}, ${state}`}
                                />
                            </div>
                        ) : null}

                        {/* Quote Date */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_date')}</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker disabled sx={TEXTFIELD_STYLE} format="YYYY-MM-DD" value={quoteDate} />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Valid Until */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('valid_until')}</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker disabled sx={TEXTFIELD_STYLE} format="YYYY-MM-DD" value={validUntil} />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Requested Delivery Date */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('requested_delivery_date')}</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker disabled sx={TEXTFIELD_STYLE} format="YYYY-MM-DD" value={requestedDeliveryDate} />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Salesperson */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('salesperson_owner')}</p>
                            <div className='w-full'>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    options={[]}
                                    value={salespersonId ? { label: salespersonName, id: salespersonId } : null}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>
                        </div>

                        {/* Quote Status */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_status')}</p>
                            <div className='w-full'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select disabled value={quoteStatus} sx={TEXTFIELD_STYLE}>
                                        {quoteStatuses.map((status) => (
                                            <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>

                        {/* Customer Reference */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('customer_reference')}</p>
                            <div className='w-full'>
                                <TextField sx={TEXTFIELD_STYLE} value={customerReference} disabled />
                            </div>
                        </div>
                    </div>

                    <div className='w-full lg:w-3/5 h-fit'>
                        <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                            <div className='pt-1 pl-3 p-5'>
                                <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('quote_items')}</p>
                            </div>
                            <div className='border-b' style={{ opacity: 0.4, background: '#FCFCFC' }}>
                                <TableShared columns={getColumns()} items={quoteItems} disableColumnMenu={true} />
                            </div>
                        </div>

                        <div className='w-full'>
                            <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                                <div className='pt-1 p-5'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('comments')}</p>
                                </div>
                                <div className='w-full p-5 pt-0'>
                                    <p className="text-[14px] text-[#718096]">{t('comments_text')}</p>
                                    <TextField multiline rows={3} value={comments} sx={TEXTFIELD_STYLE} disabled />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
        </>
    )
}

export default ShowQuote
